import React, { useState, useEffect, useCallback } from "react";
import {
  get_unknown_location_report,
  get_unknown_session_status,
  get_unknown_person_detail,
  label_unknown_person,
} from "services/api-service";
import { REACT_APP_API_URL, API_ROUTES } from "config/api-config"; // Update path if needed

// ============================================================
// HELPERS
// ============================================================
const formatTime = (t) => (t ? t.slice(0, 8) : "—");
const formatDate = (d) => (d ? d.split("T")[0] : "—");
const round2 = (n) => (n ? Math.round(n * 100) / 100 : 0);

const visitCountColor = (count) => {
  if (count >= 10) return { bg: "#d1fae5", color: "#065f46" };
  if (count >= 3)  return { bg: "#dbeafe", color: "#1e40af" };
  return                  { bg: "#f3f4f6", color: "#374151" };
};

// ============================================================
// COMPONENT: AsyncImage
// ============================================================
function AsyncImage({ src, alt, style }) {
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState(false);

  return (
    <div
      style={{
        position: "relative",
        width: style.width || "100%",
        maxWidth: style.maxWidth || "130px",
        height: style.height || "130px",
        display: "inline-block",
      }}
    >
      {!hasLoaded && !error && (
        <div
          style={{
            position: "absolute", top: 0, left: 0, width: "100%", height: "100%",
            borderRadius: style.borderRadius || "6px", backgroundColor: "#f3f4f6",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "#6b7280", fontSize: "11px", fontWeight: "500", border: "1px dashed #d1d5db", zIndex: 1,
          }}
        >
          ⏳ Loading...
        </div>
      )}
      {error && (
        <div
          style={{
            position: "absolute", top: 0, left: 0, width: "100%", height: "100%",
            borderRadius: style.borderRadius || "6px", backgroundColor: "#fee2e2",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "#ef4444", fontSize: "11px", fontWeight: "500", border: "1px solid #fca5a5", zIndex: 1,
          }}
        >
          ⚠️ Failed
        </div>
      )}
      <img
        src={src}
        alt={alt}
        loading="lazy"
        style={{
          ...style,
          opacity: hasLoaded ? 1 : 0,
          transition: "opacity 0.2s ease-in-out",
        }}
        onLoad={() => setHasLoaded(true)}
        onError={() => setError(true)}
      />
    </div>
  );
}

// ============================================================
// MAIN COMPONENT
// ============================================================
export default function UnknownAttendanceTable() {
  const today = new Date().toISOString().split("T")[0];

  const [activeView, setActiveView] = useState("daily");
  const [selectedDate, setSelectedDate]   = useState(today);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [records, setRecords]             = useState([]);
  const [loading, setLoading]             = useState(false);

  const [filterStatus, setFilterStatus]   = useState(""); 
  const [filterCamera, setFilterCamera]   = useState("");

  const [rangeStart, setRangeStart] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 7);
    return d.toISOString().split("T")[0];
  });
  const [rangeEnd, setRangeEnd]         = useState(today);
  const [rangeData, setRangeData]       = useState([]);
  const [rangeLoading, setRangeLoading] = useState(false);

  const [detailId, setDetailId]         = useState(null);
  const [detail, setDetail]             = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [labelId, setLabelId]     = useState(null);
  const [labelText, setLabelText] = useState("");
  const [labelSaving, setLabelSaving] = useState(false);

  const [expandedRow, setExpandedRow] = useState(null);

  const fetchDaily = useCallback(async () => {
    if (!selectedDate) return;
    try {
      setLoading(true);
      const payload = {
        date: selectedDate,
        page_size: 5000,
        include_images: false,
      };
      if (selectedLocation && selectedLocation.trim()) {
        payload.location = selectedLocation.trim();
      }
      const res = await get_unknown_session_status(payload);
      const d = res?.data || {};
      const all = d.present?.data || [];
      setRecords(all);
    } catch (err) {
      console.error("❌ Unknown daily fetch error:", err);
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [selectedDate, selectedLocation]);

  const fetchRange = useCallback(async () => {
    if (!rangeStart || !rangeEnd) return;
    try {
      setRangeLoading(true);
      const res = await get_unknown_location_report({
        start_date: rangeStart,
        end_date: rangeEnd,
        include_date_breakdown: true,
      });

      const rows = [];
      Object.entries(res?.data || {}).forEach(([dateKey, locations]) => {
        Object.entries(locations).forEach(([loc, stats]) => {
          (stats.datedata?.date_breakdown || []).forEach(bd => {
            rows.push({
              date: bd.date,
              location: loc,
              total_unknowns: bd.total_unknowns,
              present: bd.present,
              completed_sessions: bd.completed_sessions,
              avg_duration_hours: bd.avg_duration_hours,
            });
          });
        });
      });
      setRangeData(rows);
    } catch (err) {
      console.error("❌ Unknown range fetch error:", err);
      setRangeData([]);
    } finally {
      setRangeLoading(false);
    }
  }, [rangeStart, rangeEnd]);

  useEffect(() => {
    if (activeView === "daily") fetchDaily();
  }, [activeView, fetchDaily]);

  const openDetail = async (unknownId) => {
    setDetailId(unknownId);
    setDetail(null);
    setDetailLoading(true);
    try {
      const res = await get_unknown_person_detail({
        unknown_id: unknownId,
        include_images: true,
      });
      setDetail(res?.data || null);
    } catch (err) {
      console.error("❌ Unknown detail fetch error:", err);
    } finally {
      setDetailLoading(false);
    }
  };

  const saveLabel = async () => {
    if (!labelId || !labelText.trim()) return;
    try {
      setLabelSaving(true);
      await label_unknown_person({ unknown_id: labelId, label: labelText.trim() });
      setRecords(prev => prev.map(r =>
        r.unknown_id === labelId ? { ...r, label: labelText.trim() } : r
      ));
      setLabelId(null);
      setLabelText("");
      alert("✅ Label saved!");
    } catch (err) {
      console.error("❌ Label save error:", err);
      alert("Failed to save label.");
    } finally {
      setLabelSaving(false);
    }
  };

  const filtered = records.filter(r => {
    if (filterStatus === "open")      return r.check_out_time === null;
    if (filterStatus === "completed") return r.check_out_time !== null;
    if (filterCamera)                 return r.location === filterCamera;
    return true;
  });

  const uniqueLocations = [...new Set(records.map(r => r.location).filter(Boolean))].sort();

  const openCount      = records.filter(r => r.check_out_time === null).length;
  const completedCount = records.filter(r => r.check_out_time !== null).length;
  const avgDuration    = records.length > 0
    ? round2(records.reduce((s, r) => s + (r.total_duration_hours || 0), 0) / records.length)
    : 0;

  const rangeTotalDetections = rangeData.reduce((s, r) => s + (r.total_unknowns || 0), 0);
  const rangeAvgDuration     = rangeData.length > 0
    ? round2(rangeData.reduce((s, r) => s + (r.avg_duration_hours || 0), 0) / rangeData.length)
    : 0;

  return (
    <div style={{ backgroundColor: "#f8f9fa", minHeight: "100vh", padding: "24px" }}>
      <div style={{ marginBottom: "24px" }}>
        <h1 style={{ fontSize: "32px", fontWeight: "600", color: "#1a1a1a", margin: "0 0 20px 0" }}>
          🕵️ Unknown Persons Tracking
        </h1>

        <div style={{ display: "flex", gap: "8px", marginBottom: "24px", borderBottom: "2px solid #e5e7eb" }}>
          {[
            { key: "daily", label: "📅 Daily View" },
            { key: "range", label: "📊 Date Range Report" },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveView(tab.key)}
              style={{
                padding: "12px 24px", color: activeView === tab.key ? "#f97316" : "#6b7280", border: "none",
                borderBottom: activeView === tab.key ? "3px solid #f97316" : "none", backgroundColor: "transparent",
                cursor: "pointer", fontWeight: activeView === tab.key ? "600" : "500", fontSize: "15px", transition: "all 0.2s ease",
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeView === "daily" && (
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "center", marginBottom: "16px" }}>
            <input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} style={{ padding: "10px 12px", borderRadius: "6px", border: "1px solid #d0d0d0", fontSize: "14px", background: "white" }} />
            <input type="text" placeholder="Filter by location..." value={selectedLocation} onChange={e => setSelectedLocation(e.target.value)} style={{ padding: "10px 12px", borderRadius: "6px", border: "1px solid #d0d0d0", fontSize: "14px", background: "white", minWidth: "180px" }} />
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ padding: "10px 12px", borderRadius: "6px", border: "1px solid #d0d0d0", fontSize: "14px", background: "white" }}>
              <option value="">All Sessions</option>
              <option value="open">🟡 Still Inside</option>
              <option value="completed">✅ Completed</option>
            </select>
            {uniqueLocations.length > 0 && (
              <select value={filterCamera} onChange={e => setFilterCamera(e.target.value)} style={{ padding: "10px 12px", borderRadius: "6px", border: "1px solid #d0d0d0", fontSize: "14px", background: "white" }}>
                <option value="">All Cameras</option>
                {uniqueLocations.map(loc => <option key={loc} value={loc}>📹 {loc}</option>)}
              </select>
            )}
            <button onClick={fetchDaily} style={{ padding: "10px 20px", backgroundColor: "#f97316", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: "500", fontSize: "14px" }}>🔍 Search</button>
            <button onClick={() => { setFilterStatus(""); setFilterCamera(""); setSelectedLocation(""); }} style={{ padding: "10px 16px", backgroundColor: "#6b7280", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: "500", fontSize: "14px" }}>↺ Reset</button>
          </div>
        )}

        {activeView === "range" && (
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "center", marginBottom: "16px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <label style={{ fontSize: "14px", fontWeight: "500", color: "#374151" }}>From:</label>
              <input type="date" value={rangeStart} onChange={e => setRangeStart(e.target.value)} style={{ padding: "10px 12px", borderRadius: "6px", border: "1px solid #d0d0d0", fontSize: "14px", background: "white" }} />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <label style={{ fontSize: "14px", fontWeight: "500", color: "#374151" }}>To:</label>
              <input type="date" value={rangeEnd} onChange={e => setRangeEnd(e.target.value)} style={{ padding: "10px 12px", borderRadius: "6px", border: "1px solid #d0d0d0", fontSize: "14px", background: "white" }} />
            </div>
            <button onClick={fetchRange} style={{ padding: "10px 20px", backgroundColor: "#f97316", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: "500", fontSize: "14px" }}>📊 Generate Report</button>
          </div>
        )}
      </div>

      {activeView === "daily" && (
        <>
          {!loading && records.length > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "12px", marginBottom: "24px" }}>
              {[
                { label: "Total",         value: records.length,  color: "#1a1a1a" },
                { label: "Still Inside",  value: openCount,       color: "#f97316" },
                { label: "Completed",     value: completedCount,  color: "#10b981" },
                { label: "Avg Duration",  value: `${avgDuration}h`, color: "#6366f1" },
              ].map(s => (
                <div key={s.label} style={{ backgroundColor: "white", padding: "16px", borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
                  <div style={{ fontSize: "12px", color: "#666", marginBottom: "6px" }}>{s.label}</div>
                  <div style={{ fontSize: "28px", fontWeight: "600", color: s.color }}>{s.value}</div>
                </div>
              ))}
            </div>
          )}

          {loading && <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>⏳ Loading...</div>}
          {!loading && records.length === 0 && <div style={{ backgroundColor: "white", padding: "40px", borderRadius: "8px", textAlign: "center", color: "#999" }}>No unknown persons detected on this date.</div>}

          {!loading && filtered.length > 0 && (
            <div style={{ backgroundColor: "white", borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", overflow: "hidden", marginBottom: "20px" }}>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ backgroundColor: "#f3f4f6", borderBottom: "1px solid #e5e7eb" }}>
                      {["ID", "Label", "Camera / Location", "Check-In", "Check-Out", "Duration", "Visits", "Session", "Actions"].map(h => (
                        <th key={h} style={{ padding: "12px 16px", textAlign: h === "Duration" ? "right" : "left", fontSize: "13px", fontWeight: "600", color: "#374151", whiteSpace: "nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((rec, idx) => {
                      const isOpen      = rec.check_out_time === null;
                      const vcColors    = visitCountColor(rec.visit_count || 1);
                      const isExpanded  = expandedRow === rec.id;

                      return (
                        <React.Fragment key={rec.id}>
                          <tr style={{ borderBottom: "1px solid #e5e7eb", backgroundColor: idx % 2 === 0 ? "#fff" : "#f9fafb" }}>
                            <td style={{ padding: "12px 16px", fontSize: "12px", color: "#6366f1", fontFamily: "monospace", fontWeight: "600", whiteSpace: "nowrap" }}>{rec.unknown_id}</td>
                            <td style={{ padding: "12px 16px", fontSize: "13px", color: "#374151" }}>
                              {rec.label ? <span style={{ padding: "3px 8px", borderRadius: "4px", background: "#fef9c3", color: "#713f12", fontSize: "12px", fontWeight: "500" }}>🏷️ {rec.label}</span> : <span style={{ color: "#9ca3af", fontSize: "12px" }}>—</span>}
                            </td>
                            <td style={{ padding: "12px 16px", fontSize: "13px", color: "#666" }}>📹 {rec.location || "—"}</td>
                            <td style={{ padding: "12px 16px", fontSize: "13px", color: "#374151" }}>{formatTime(rec.check_in_time)}</td>
                            <td style={{ padding: "12px 16px", fontSize: "13px" }}>{isOpen ? <span style={{ color: "#f97316", fontWeight: "600", fontSize: "12px" }}>⏳ Still Inside</span> : formatTime(rec.check_out_time)}</td>
                            <td style={{ padding: "12px 16px", fontSize: "13px", color: "#666", textAlign: "right" }}>{round2(rec.total_duration_hours)}h</td>
                            <td style={{ padding: "12px 16px" }}><span style={{ padding: "3px 10px", borderRadius: "4px", fontSize: "12px", fontWeight: "600", ...vcColors }}>{rec.visit_count || 1}×</span></td>
                            <td style={{ padding: "12px 16px" }}><span style={{ padding: "4px 10px", borderRadius: "4px", fontSize: "12px", fontWeight: "600", backgroundColor: isOpen ? "#fff7ed" : "#d1fae5", color: isOpen ? "#c2410c" : "#065f46" }}>{isOpen ? "Open" : "Completed"}</span></td>
                            <td style={{ padding: "12px 16px" }}>
                              <div style={{ display: "flex", gap: "6px" }}>
                                <button onClick={() => openDetail(rec.unknown_id)} style={{ padding: "4px 10px", backgroundColor: "#6366f1", color: "white", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "12px", fontWeight: "600" }}>Detail</button>
                                <button onClick={() => { setLabelId(rec.unknown_id); setLabelText(rec.label || ""); }} style={{ padding: "4px 10px", backgroundColor: "#f59e0b", color: "white", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "12px", fontWeight: "600" }}>Label</button>
                                <button onClick={() => setExpandedRow(isExpanded ? null : rec.id)} style={{ padding: "4px 10px", backgroundColor: "#e5e7eb", color: "#374151", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "12px", fontWeight: "600" }}>{isExpanded ? "Hide" : "Logs"}</button>
                              </div>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr style={{ backgroundColor: "#f0f4ff" }}>
                              <td colSpan="9" style={{ padding: "16px 24px" }}>
                                <p style={{ fontSize: "12px", fontWeight: "600", color: "#4f46e5", marginBottom: "8px" }}>Detection Logs — click "Detail" for full history</p>
                                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "8px", fontSize: "12px", color: "#374151" }}>
                                  <div><strong>First detected:</strong> {formatTime(rec.check_in_time)}</div>
                                  <div><strong>Last detected:</strong> {formatTime(rec.check_out_time) || "Still inside"}</div>
                                  <div><strong>Total duration:</strong> {round2(rec.total_duration_hours)}h ({rec.total_duration_minutes || 0}m)</div>
                                  <div><strong>Confidence:</strong> {rec.confidence ? `${(rec.confidence * 100).toFixed(1)}%` : "—"}</div>
                                  <div><strong>Camera #:</strong> {rec.camera_number || "—"}</div>
                                  <div><strong>First seen date:</strong> {formatDate(rec.first_seen_date)}</div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {activeView === "range" && (
        <>
          {rangeLoading && <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>📊 Generating report...</div>}
          {!rangeLoading && rangeData.length === 0 && <div style={{ backgroundColor: "white", padding: "40px", borderRadius: "8px", textAlign: "center", color: "#999" }}>Click "Generate Report" to fetch data for the selected date range.</div>}
          {!rangeLoading && rangeData.length > 0 && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "12px", marginBottom: "24px" }}>
                {[
                  { label: "Total Detections",    value: rangeTotalDetections, color: "#f97316" },
                  { label: "Days Covered",         value: rangeData.length,     color: "#6366f1" },
                  { label: "Avg Duration",          value: `${rangeAvgDuration}h`, color: "#10b981" },
                  { label: "Date Range",            value: `${rangeStart} → ${rangeEnd}`, color: "#374151", small: true },
                ].map(s => (
                  <div key={s.label} style={{ backgroundColor: "white", padding: "16px", borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
                    <div style={{ fontSize: "12px", color: "#666", marginBottom: "6px" }}>{s.label}</div>
                    <div style={{ fontSize: s.small ? "13px" : "28px", fontWeight: "600", color: s.color }}>{s.value}</div>
                  </div>
                ))}
              </div>
              <div style={{ backgroundColor: "white", borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", overflow: "hidden" }}>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ backgroundColor: "#f3f4f6", borderBottom: "1px solid #e5e7eb" }}>
                        {["Date", "Camera / Location", "Total Unknowns", "Present", "Completed Sessions", "Avg Duration"].map(h => <th key={h} style={{ padding: "12px 16px", textAlign: "left", fontSize: "13px", fontWeight: "600", color: "#374151", whiteSpace: "nowrap" }}>{h}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {rangeData.map((row, idx) => (
                        <tr key={`${row.date}-${row.location}`} style={{ borderBottom: "1px solid #e5e7eb", backgroundColor: idx % 2 === 0 ? "#fff" : "#f9fafb" }}>
                          <td style={{ padding: "12px 16px", fontSize: "14px", fontWeight: "500", color: "#374151" }}>{row.date}</td>
                          <td style={{ padding: "12px 16px", fontSize: "13px", color: "#666" }}>📹 {row.location || "ALL"}</td>
                          <td style={{ padding: "12px 16px", fontSize: "14px", fontWeight: "600", color: "#f97316" }}>{row.total_unknowns}</td>
                          <td style={{ padding: "12px 16px" }}><span style={{ padding: "3px 10px", borderRadius: "4px", fontSize: "12px", fontWeight: "600", backgroundColor: "#d1fae5", color: "#065f46" }}>{row.present}</span></td>
                          <td style={{ padding: "12px 16px", fontSize: "14px", color: "#6366f1", fontWeight: "600" }}>{row.completed_sessions}</td>
                          <td style={{ padding: "12px 16px", fontSize: "14px", color: "#374151" }}>{row.avg_duration_hours}h</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* LABEL MODAL */}
      {labelId && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: "white", borderRadius: "10px", padding: "28px", width: "400px", maxWidth: "90vw", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
            <h2 style={{ fontSize: "18px", fontWeight: "600", color: "#0f172a", marginBottom: "8px" }}>🏷️ Label Unknown Person</h2>
            <p style={{ fontSize: "13px", color: "#64748b", marginBottom: "16px", fontFamily: "monospace" }}>{labelId}</p>
            <input type="text" value={labelText} onChange={e => setLabelText(e.target.value)} placeholder='e.g. "Regular Visitor"' style={{ width: "100%", padding: "10px 12px", borderRadius: "6px", border: "1px solid #d0d0d0", fontSize: "14px", marginBottom: "16px", boxSizing: "border-box" }} />
            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <button onClick={() => { setLabelId(null); setLabelText(""); }} style={{ padding: "8px 16px", background: "#f1f5f9", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: "500", fontSize: "14px", color: "#374151" }}>Cancel</button>
              <button onClick={saveLabel} disabled={labelSaving || !labelText.trim()} style={{ padding: "8px 20px", background: "#f97316", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: "600", fontSize: "14px", opacity: labelSaving ? 0.7 : 1 }}>{labelSaving ? "Saving..." : "Save Label"}</button>
            </div>
          </div>
        </div>
      )}

      {/* DETAIL MODAL */}
      {detailId && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: "16px" }}>
          <div style={{ background: "white", borderRadius: "10px", width: "700px", maxWidth: "95vw", maxHeight: "90vh", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
            <div style={{ padding: "20px 24px", borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <h2 style={{ fontSize: "18px", fontWeight: "600", color: "#0f172a", margin: 0 }}>Person Detail</h2>
                <p style={{ fontSize: "12px", color: "#64748b", margin: "4px 0 0 0", fontFamily: "monospace" }}>{detailId}</p>
              </div>
              <button onClick={() => { setDetailId(null); setDetail(null); }} style={{ background: "none", border: "none", cursor: "pointer", fontSize: "20px", color: "#64748b", padding: "4px" }}>✕</button>
            </div>

            <div style={{ padding: "20px 24px", overflowY: "auto", flex: 1 }}>
              {detailLoading && <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>⏳ Loading detail...</div>}

              {!detailLoading && detail && (
                <>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "20px" }}>
                    <div style={{ background: "#f8fafc", borderRadius: "8px", padding: "16px" }}>
                      {detail.has_image && (
                        <AsyncImage
                          src={`${REACT_APP_API_URL}/${API_ROUTES.get_unknown_master_image(detail.unknown_id)}`}
                          alt="Unknown person"
                          style={{ width: "80px", height: "80px", borderRadius: "6px", objectFit: "cover", marginBottom: "12px", border: "1px solid #e5e7eb" }}
                        />
                      )}
                      <div style={{ fontSize: "13px", color: "#374151", lineHeight: "1.8" }}>
                        <div><strong>Label:</strong> {detail.label || <span style={{ color: "#9ca3af" }}>None</span>}</div>
                        <div><strong>Visit Count:</strong> {detail.visit_count || 0}</div>
                        <div><strong>First Seen:</strong> {formatDate(detail.first_seen_date)}</div>
                        <div><strong>Last Seen:</strong> {formatDate(detail.last_seen_date)}</div>
                      </div>
                    </div>
                    <div style={{ background: "#fff7ed", borderRadius: "8px", padding: "16px" }}>
                      <div style={{ fontSize: "13px", color: "#374151", lineHeight: "1.8" }}>
                        <div><strong>Total Sessions:</strong> {detail.total_sessions || 0}</div>
                        <div><strong>Sighting Count:</strong> {detail.sighting_count || 0}</div>
                        <div><strong>Camera #:</strong> {detail.camera_number || "—"}</div>
                        <div><strong>Date Range:</strong> {detail.date_range?.start} → {detail.date_range?.end}</div>
                      </div>
                    </div>
                  </div>

                  <h3 style={{ fontSize: "14px", fontWeight: "600", color: "#374151", marginBottom: "12px" }}>Sessions ({detail.sessions?.length || 0})</h3>
                  {(detail.sessions || []).map((s, si) => (
                    <div key={s.attendance_id} style={{ background: "#f8fafc", borderRadius: "8px", padding: "14px", marginBottom: "10px", border: "1px solid #e5e7eb" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                        <div style={{ fontSize: "13px", fontWeight: "600", color: "#374151" }}>📅 {s.date} &nbsp;|&nbsp; In: {formatTime(s.check_in_time)} &nbsp;|&nbsp; Out: {s.check_out_time ? formatTime(s.check_out_time) : <span style={{ color: "#f97316" }}>Still Inside</span>}</div>
                        <span style={{ fontSize: "12px", fontWeight: "600", color: "#6366f1" }}>{round2(s.total_duration_hours)}h</span>
                      </div>
                      <div style={{ fontSize: "12px", color: "#64748b" }}>📹 {s.location || "—"} | {s.detection_logs?.length || 0} log entries</div>

                      {s.detection_logs && s.detection_logs.length > 0 && (
                        <div style={{ marginTop: "10px", display: "flex", flexDirection: "column", gap: "4px" }}>
                          {s.detection_logs.map((log, li) => (
                            <div key={log.id} style={{ display: "flex", gap: "12px", fontSize: "11px", color: "#6b7280", padding: "4px 8px", background: "white", borderRadius: "4px", alignItems: "center" }}>
                              <span style={{ fontWeight: "600", color: log.direction === "entry" ? "#10b981" : "#ef4444" }}>{log.direction === "entry" ? "→ IN" : "← OUT"}</span>
                              <span>{log.detection_time ? log.detection_time.slice(11, 19) : "—"}</span>
                              <span>{log.detection_type}</span>
                              <span style={{ color: "#9ca3af" }}>conf: {log.confidence ? `${(log.confidence * 100).toFixed(1)}%` : "—"}</span>
                              {log.duration_hours ? <span>⏱ {round2(log.duration_hours)}h</span> : null}
                              {log.has_detected_face && (
                                <AsyncImage
                                  src={`${REACT_APP_API_URL}/${API_ROUTES.get_unknown_log_image(log.id)}`}
                                  alt=""
                                  style={{ width: "30px", height: "30px", borderRadius: "3px", objectFit: "cover" }}
                                />
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
